/**
 * FEAT-084 — voice fidelity: the thresholds, and the geometry that draws them.
 *
 * Shared between the API and the SPA for the same reason `x-thread.ts` is: the
 * orchestrator decides whether a piece is gated, and the piece screen draws the
 * line it was gated against. If those two numbers ever disagree, the screen is
 * lying about why a piece stopped.
 *
 * Pure. No imports, no framework.
 */

/** Each dimension is scored 0-10 by the Voice Match Evaluator. */
export const VOICE_MAX = 10;

/**
 * Below this on ANY single dimension, the piece is gated — even when the total
 * looks fine. 10+10+4+4 sums to exactly the aggregate threshold and still has
 * two broken dimensions.
 */
export const VOICE_CATEGORY_FLOOR = 6;

/** Below this in total, the piece is gated. Mirrors qualityScore's 70/100 ratio. */
export const VOICE_AGGREGATE_GATE = 28;

/** Sum of a clean run. Four dimensions, ten each. */
export const VOICE_AGGREGATE_MAX = 40;

export const VOICE_DIMENSION_LABELS = {
  voiceMatchScore: "Voice match",
  rhythmScore: "Rhythm",
  voiceStructureScore: "Structure",
  vocabularyScore: "Vocabulary",
} as const;

export type VoiceDimensionKey = keyof typeof VOICE_DIMENSION_LABELS;

export const VOICE_DIMENSION_KEYS = [
  "voiceMatchScore",
  "rhythmScore",
  "voiceStructureScore",
  "vocabularyScore",
] as const;

export interface VoiceScoreSource {
  voice_match_score?: number | null;
  rhythm_score?: number | null;
  voice_structure_score?: number | null;
  vocabulary_score?: number | null;
}

const SNAKE: Record<VoiceDimensionKey, keyof VoiceScoreSource> = {
  voiceMatchScore: "voice_match_score",
  rhythmScore: "rhythm_score",
  voiceStructureScore: "voice_structure_score",
  vocabularyScore: "vocabulary_score",
};

export interface VoiceDimension {
  key: VoiceDimensionKey;
  label: string;
  score: number | null;
  /** Below the per-category floor. False when unscored — absence is not failure. */
  under: boolean;
  /** Bar width as a percentage of the track. 0 when unscored. */
  percent: number;
}

export function voiceDimensions(source: VoiceScoreSource): VoiceDimension[] {
  return VOICE_DIMENSION_KEYS.map((key) => {
    const raw = source[SNAKE[key]];
    const score = typeof raw === "number" ? raw : null;
    return {
      key,
      label: VOICE_DIMENSION_LABELS[key],
      score,
      under: score !== null && score < VOICE_CATEGORY_FLOOR,
      percent: score === null ? 0 : Math.max(0, Math.min(100, (score / VOICE_MAX) * 100)),
    };
  });
}

/**
 * The total the gate reads, or null when the evaluator has not produced all
 * four. Partial scores deliberately have no aggregate: summing two and
 * comparing to a four-score threshold would read as a catastrophic result.
 */
export function voiceAggregate(source: VoiceScoreSource): number | null {
  const values = VOICE_DIMENSION_KEYS.map((k) => source[SNAKE[k]]).filter(
    (v): v is number => typeof v === "number",
  );
  if (values.length !== VOICE_DIMENSION_KEYS.length) return null;
  return values.reduce((a, b) => a + b, 0);
}

/**
 * Whether this piece is gated on voice, and why. Mirrors the orchestrator's
 * `voiceGateTrips` — same two conditions, same "nulls never block" contract.
 */
export type VoiceGateState = "unscored" | "pass" | "under_floor" | "under_aggregate";

export function voiceGateState(source: VoiceScoreSource): VoiceGateState {
  const dims = voiceDimensions(source);
  const scored = dims.filter((d) => d.score !== null);
  if (scored.length === 0) return "unscored";
  if (scored.some((d) => d.under)) return "under_floor";
  const total = voiceAggregate(source);
  if (total !== null && total < VOICE_AGGREGATE_GATE) return "under_aggregate";
  return "pass";
}

/** Where the gate's tick sits on the aggregate track, as a percentage. */
export const VOICE_AGGREGATE_GATE_PERCENT =
  (VOICE_AGGREGATE_GATE / VOICE_AGGREGATE_MAX) * 100;

/** Where the floor's tick sits on a single dimension's track, as a percentage. */
export const VOICE_FLOOR_PERCENT = (VOICE_CATEGORY_FLOOR / VOICE_MAX) * 100;
